import { getApiBaseUrl } from '@/lib/api/client';
import { getAuthToken } from '@/lib/api/token-store';

/** File lists stay height-auto until this many rows; above that, use ScrollArea with max height. */
export const ATTACHMENT_FILE_LIST_SCROLL_THRESHOLD = 3;

/** Legacy WhatsApp-style encrypted payload MIME before server normalization. */
export const STICKER_MIME_TYPE = 'application/was';

/** Shared max size for sticker previews (chat + composer). */
export const STICKER_PREVIEW_IMAGE_CLASS =
  'h-36 w-36 max-h-[min(9rem,42vw)] max-w-[min(9rem,42vw)] select-none object-contain [image-rendering:auto] drop-shadow-[0_6px_16px_rgba(15,23,42,0.2)] dark:drop-shadow-[0_8px_22px_rgba(0,0,0,0.55)]';

/**
 * Server / client rule: WhatsApp Lottie sticker when flagged or MIME is *.was.
 * @param {Record<string, unknown>} att
 */
export function isWhatsappLottieStickerType(att) {
  if (!att || typeof att !== 'object') {
    return false;
  }
  if (att.isLottie === true) {
    return true;
  }
  const mime = String(att.mimeType || att.contentType || '').toLowerCase();
  return mime === STICKER_MIME_TYPE || mime.endsWith('/was');
}

/**
 * Sticker attachment in UI (normalized WebP / JSON / dotLottie / legacy .was, or explicit kind).
 * @param {Record<string, unknown>} att
 */
export function attachmentIsSticker(att) {
  if (!att || typeof att !== 'object') {
    return false;
  }
  if (isWhatsappLottieStickerType(att)) {
    return true;
  }
  const kind = String(att.kind || '').toLowerCase();
  if (kind === 'sticker') {
    return true;
  }
  const fn = String(att.filename || att.name || '').toLowerCase();
  if (fn.endsWith('sticker.webp')) {
    return true;
  }
  const mime = String(att.mimeType || att.contentType || '').toLowerCase();
  if (mime.startsWith('image/webp') && fn.includes('sticker')) {
    return true;
  }
  return false;
}

/**
 * How to render a sticker after server normalization (or legacy .was).
 * @param {Record<string, unknown>} att
 * @returns {'webp' | 'video' | 'was'}
 */
export function stickerPresentationMode(att) {
  const mime = String(att.mimeType || att.contentType || '').toLowerCase();
  const fn = String(att.filename || att.name || '').toLowerCase();

  if (mime.startsWith('video/')) {
    return 'video';
  }
  if (mime.startsWith('image/') || fn.endsWith('sticker.webp')) {
    return 'webp';
  }
  return 'was';
}

/** @param {Record<string, unknown>} o */
function isBodymovinWithDrawableLayers(o) {
  const top = Array.isArray(o.layers) ? o.layers.length : 0;
  const charsCount = Array.isArray(o.chars) ? o.chars.length : 0;
  const assetsNested =
    Array.isArray(o.assets) &&
    o.assets.some((a) => {
      if (!a || typeof a !== 'object' || Array.isArray(a)) {
        return false;
      }
      const L = /** @type {Record<string, unknown>} */ (a).layers;
      return Array.isArray(L) && L.length > 0;
    });
  const drawable = top > 0 || charsCount > 0 || assetsNested;
  if (!drawable) {
    return false;
  }
  const version = typeof o.v === 'string';
  return version || typeof o.fr === 'number' || o.op !== undefined || o.ip !== undefined;
}

/**
 * Bodymovin object for lottie-web, or null (unwraps envelopes; skips empty layers).
 * @param {unknown} parsed
 * @returns {Record<string, unknown>|null}
 */
export function extractLottieAnimationData(parsed) {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }
  const root = /** @type {Record<string, unknown>} */ (parsed);
  /** @type {unknown[]} */
  const candidates = [root];
  for (const key of ['animation', 'lottie', 'data']) {
    const v = root[key];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      candidates.push(v);
    }
  }
  const anims = root.animations;
  if (Array.isArray(anims)) {
    for (const a of anims) {
      if (a && typeof a === 'object' && !Array.isArray(a)) {
        candidates.push(a);
      }
    }
  }
  for (const c of candidates) {
    const o = /** @type {Record<string, unknown>} */ (c);
    if (isBodymovinWithDrawableLayers(o)) {
      return o;
    }
  }
  return null;
}

/**
 * @param {ArrayBuffer} ab
 * @returns {ArrayBuffer|null} minified JSON bytes
 */
function tryUtf8LottieFromArrayBuffer(ab) {
  const u = new Uint8Array(ab);
  if (u.length < 2 || u[0] !== 0x7b) {
    return null;
  }
  try {
    const text = new TextDecoder('utf-8', { fatal: false }).decode(ab);
    const parsed = JSON.parse(text);
    const extracted = extractLottieAnimationData(parsed);
    if (!extracted) {
      return null;
    }
    return new TextEncoder().encode(JSON.stringify(extracted)).buffer;
  } catch {
    return null;
  }
}

/**
 * @param {ArrayBuffer} ab
 * @returns {Promise<ArrayBuffer|null>}
 */
async function decompressGzip(ab) {
  const u = new Uint8Array(ab);
  if (u.length < 2 || u[0] !== 0x1f || u[1] !== 0x8b) {
    return null;
  }
  try {
    const ds = new DecompressionStream('gzip');
    return await new Response(new Blob([ab]).stream().pipeThrough(ds)).arrayBuffer();
  } catch {
    return null;
  }
}

/**
 * Strip up to `max` gzip wrappers (.tgs / some WhatsApp payloads are gzip-on-gzip).
 * @param {ArrayBuffer} ab
 * @param {number} [max]
 * @returns {Promise<ArrayBuffer>}
 */
async function unwrapStickerGzipLayers(ab, max = 4) {
  let cur = ab;
  for (let i = 0; i < max; i += 1) {
    const u = new Uint8Array(cur);
    if (u.length < 2 || u[0] !== 0x1f || u[1] !== 0x8b) {
      break;
    }
    const prevLen = cur.byteLength;
    const next = await decompressGzip(cur);
    if (!next || next.byteLength === 0 || next.byteLength >= prevLen) {
      break;
    }
    cur = next;
  }
  return cur;
}

/**
 * @param {ArrayBuffer} ab
 * @returns {Promise<ArrayBuffer|null>}
 */
async function decompressBrotli(ab) {
  try {
    const ds = new DecompressionStream('brotli');
    return await new Response(new Blob([ab]).stream().pipeThrough(ds)).arrayBuffer();
  } catch {
    return null;
  }
}

/**
 * Raw zlib / deflate (Node `inflateSync` analogue); best-effort in browsers.
 * @param {ArrayBuffer} ab
 * @returns {Promise<ArrayBuffer|null>}
 */
async function decompressZlibInflate(ab) {
  try {
    const ds = new DecompressionStream('deflate');
    return await new Response(new Blob([ab]).stream().pipeThrough(ds)).arrayBuffer();
  } catch {
    return null;
  }
}

/**
 * Parse a minimal ZIP local-file-entry and extract the first *.json that looks like Lottie.
 * WhatsApp `.was` stickers are often a ZIP with `animation.json` + `trust_token` inside.
 *
 * ZIP local file header layout (all LE):
 *   0-3  signature 0x04034b50
 *   8-9  compression method (0=stored, 8=deflate, 9=deflate64 ignored)
 *   18-21 compressed size
 *   26-27 filename length
 *   28-29 extra field length
 *   30..  filename, extra, data
 *
 * @param {ArrayBuffer} ab
 * @returns {Promise<ArrayBuffer|null>} minified Lottie JSON bytes, or null
 */
async function extractJsonFromStickerZip(ab) {
  const u = new Uint8Array(ab);
  const dv = new DataView(ab);
  if (u.length < 30 || u[0] !== 0x50 || u[1] !== 0x4b || u[2] !== 0x03 || u[3] !== 0x04) {
    return null;
  }
  let offset = 0;
  while (offset + 30 <= u.length) {
    if (u[offset] !== 0x50 || u[offset + 1] !== 0x4b) break;
    if (u[offset + 2] === 0x01 && u[offset + 3] === 0x02) break;
    if (u[offset + 2] !== 0x03 || u[offset + 3] !== 0x04) break;

    const method = dv.getUint16(offset + 8, true);
    const compressedSize = dv.getUint32(offset + 18, true);
    const filenameLen = dv.getUint16(offset + 26, true);
    const extraLen = dv.getUint16(offset + 28, true);
    const filename = new TextDecoder('utf-8', { fatal: false }).decode(
      u.subarray(offset + 30, offset + 30 + filenameLen),
    );
    const dataStart = offset + 30 + filenameLen + extraLen;

    if (filename.endsWith('.json')) {
      const compData = ab.slice(dataStart, dataStart + compressedSize);
      let decompressed = null;
      if (method === 0) {
        decompressed = compData;
      } else if (method === 8) {
        try {
          const ds = new DecompressionStream('deflate-raw');
          decompressed = await new Response(new Blob([compData]).stream().pipeThrough(ds)).arrayBuffer();
        } catch {
          decompressed = null;
        }
      }
      if (decompressed) {
        const jsonBuf = tryUtf8LottieFromArrayBuffer(decompressed);
        if (jsonBuf) return jsonBuf;
      }
    }

    offset = dataStart + compressedSize;
    if (compressedSize === 0 && filenameLen === 0) break;
  }
  return null;
}

/**
 * Same idea as whatsup_bot `convertWhatsappLottieStickerPayload` (wsBridge.js):
 * RIFF/WebP → image, UTF-8 Lottie JSON → application/json, gzip/brotli/deflate → Lottie JSON, PK → dotLottie zip.
 * Returns null to keep original `application/was` handling downstream.
 *
 * @param {ArrayBuffer} buffer
 * @returns {Promise<{ buffer: ArrayBuffer, mimeType: string, filename: string } | null>}
 */
export async function convertWhatsappWasStickerPayload(buffer) {
  if (!buffer || buffer.byteLength === 0) {
    return null;
  }
  const ab = buffer;
  const u = new Uint8Array(ab);

  if (ab.byteLength >= 12) {
    const riff = String.fromCharCode(u[0], u[1], u[2], u[3]);
    const webp = String.fromCharCode(u[8], u[9], u[10], u[11]);
    if (riff === 'RIFF' && webp === 'WEBP') {
      return { buffer: ab, mimeType: 'image/webp', filename: 'sticker.webp' };
    }
  }

  const gzipStripped = await unwrapStickerGzipLayers(ab);
  let jsonAb = tryUtf8LottieFromArrayBuffer(gzipStripped);
  if (jsonAb) {
    return { buffer: jsonAb, mimeType: 'application/json', filename: 'sticker-lottie.json' };
  }

  const br = await decompressBrotli(ab);
  if (br) {
    jsonAb = tryUtf8LottieFromArrayBuffer(await unwrapStickerGzipLayers(br));
    if (jsonAb) {
      return { buffer: jsonAb, mimeType: 'application/json', filename: 'sticker-lottie.json' };
    }
  }

  const inflated = await decompressZlibInflate(ab);
  if (inflated) {
    jsonAb = tryUtf8LottieFromArrayBuffer(await unwrapStickerGzipLayers(inflated));
    if (jsonAb) {
      return { buffer: jsonAb, mimeType: 'application/json', filename: 'sticker-lottie.json' };
    }
  }

  if (u.length >= 2 && u[0] === 0x50 && u[1] === 0x4b) {
    const jsonFromZip = await extractJsonFromStickerZip(ab);
    if (jsonFromZip) {
      return { buffer: jsonFromZip, mimeType: 'application/json', filename: 'sticker-lottie.json' };
    }
    return { buffer: ab, mimeType: 'application/zip', filename: 'sticker.lottie' };
  }

  return null;
}

/**
 * Detect real container from magic bytes (legacy application/was after decrypt).
 * @param {ArrayBuffer} buffer
 * @returns {{ mime: string, mode: 'image' | 'video' } | null}
 */
function sniffStickerPayloadFormat(buffer) {
  const u = new Uint8Array(buffer);
  if (u.length < 12) {
    return null;
  }
  if (u[0] === 0x47 && u[1] === 0x49 && u[2] === 0x46 && u[3] === 0x38 && (u[4] === 0x37 || u[4] === 0x39) && u[5] === 0x61) {
    return { mime: 'image/gif', mode: 'image' };
  }
  if (
    u[0] === 0x89 &&
    u[1] === 0x50 &&
    u[2] === 0x4e &&
    u[3] === 0x47 &&
    u[4] === 0x0d &&
    u[5] === 0x0a &&
    u[6] === 0x1a &&
    u[7] === 0x0a
  ) {
    return { mime: 'image/png', mode: 'image' };
  }
  if (u[0] === 0xff && u[1] === 0xd8 && u[2] === 0xff) {
    return { mime: 'image/jpeg', mode: 'image' };
  }
  if (u[0] === 0x52 && u[1] === 0x49 && u[2] === 0x46 && u[3] === 0x46 && u.length >= 12) {
    const tag = String.fromCharCode(u[8], u[9], u[10], u[11]);
    if (tag === 'WEBP') {
      return { mime: 'image/webp', mode: 'image' };
    }
  }
  if (u.length >= 16 && u[4] === 0x66 && u[5] === 0x74 && u[6] === 0x79 && u[7] === 0x70) {
    const brand = String.fromCharCode(u[8], u[9], u[10], u[11]).toLowerCase();
    if (brand === 'avif' || brand === 'avis') {
      return { mime: 'image/avif', mode: 'image' };
    }
    if (brand === 'mif1' || brand === 'msf1' || brand === 'heic' || brand === 'heix') {
      return { mime: 'image/heic', mode: 'image' };
    }
    return { mime: 'video/mp4', mode: 'video' };
  }
  if (u[0] === 0x1a && u[1] === 0x45 && u[2] === 0xdf && u[3] === 0xa3) {
    return { mime: 'video/webm', mode: 'video' };
  }
  return null;
}

/**
 * Legacy .was / unknown sticker bytes → Blob the browser can decode (incl. animated WebP/GIF).
 * @param {Blob} blob
 * @returns {Promise<{ blob: Blob, mode: 'image' | 'video' }>}
 */
export async function normalizeStickerBlobForDisplay(blob) {
  const declared = (blob.type || '').toLowerCase();
  if (declared.startsWith('image/') && declared !== STICKER_MIME_TYPE) {
    return { blob, mode: 'image' };
  }
  if (declared.startsWith('video/')) {
    return { blob, mode: 'video' };
  }

  const buf = await blob.arrayBuffer();
  const sniffed = sniffStickerPayloadFormat(buf);
  if (sniffed) {
    return {
      blob: new Blob([buf], { type: sniffed.mime }),
      mode: sniffed.mode,
    };
  }

  if (declared === STICKER_MIME_TYPE || !declared || declared === 'application/octet-stream') {
    return {
      blob: new Blob([buf], { type: 'image/png' }),
      mode: 'image',
    };
  }

  return { blob, mode: 'image' };
}

/**
 * @param {File} file
 * @returns {'webp' | 'video' | 'was' | null}
 */
export function localStickerPresentationMode(file) {
  if (!file) {
    return null;
  }
  const mime = String(file.type || '').toLowerCase();
  const fn = String(file.name || '').toLowerCase();
  if (mime.startsWith('video/')) {
    return 'video';
  }
  if (mime.startsWith('image/') || fn.endsWith('sticker.webp')) {
    return 'webp';
  }
  if (mime === STICKER_MIME_TYPE || mime.endsWith('/was')) {
    return 'was';
  }
  return null;
}

/** @param {Record<string, unknown>|null|undefined} message */
export function parseAttachments(message) {
  const raw = message && message.attachments;
  if (raw == null) {
    return [];
  }
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      if (parsed && typeof parsed === 'object') {
        return [parsed];
      }
    } catch (_e) {
      return [];
    }
    return [];
  }
  if (typeof raw === 'object') {
    return [raw];
  }
  return [];
}

/**
 * Returns pathname (+ search) for authorized GET to fetch attachment bytes.
 * @param {Record<string, unknown>} att
 */
export function attachmentDownloadPath(att) {
  if (!att || typeof att !== 'object') {
    return '';
  }
  const url = String(att.url || '').trim();
  if (url) {
    if (url.startsWith('/')) {
      return url.split('#')[0];
    }
    try {
      const u = new URL(url);
      return u.pathname + u.search;
    } catch (_e) {
      return url.split('#')[0];
    }
  }
  const fileId = String(att.fileId || '').trim();
  if (fileId) {
    return `/api/media/${encodeURIComponent(fileId)}`;
  }
  if (att.documentId != null) {
    const docId = String(att.documentId).trim();
    if (docId) {
      return `/api/media/${encodeURIComponent(docId)}`;
    }
  }
  return '';
}

/** Attachments with stored media (not inline base64 waiting for upload). */
export function getRenderableStoredAttachments(message) {
  return filterRenderableStoredAttachments(parseAttachments(message));
}

/**
 * @param {unknown[]} attachmentsRaw
 */
export function filterRenderableStoredAttachments(attachmentsRaw) {
  const all = Array.isArray(attachmentsRaw) ? attachmentsRaw : [];
  return all.filter((a) => {
    if (!a || typeof a !== 'object') {
      return false;
    }
    if (a.data != null && String(a.data).trim()) {
      return false;
    }
    return Boolean(attachmentDownloadPath(a));
  });
}

/**
 * Rich inline preview in chat only for image / video / audio (not PDF or other types).
 * @param {Record<string, unknown>} att
 * @returns {'image' | 'video' | 'audio' | null}
 */
export function attachmentMediaPreviewVariant(att) {
  if (!att || typeof att !== 'object') {
    return null;
  }
  if (attachmentIsSticker(att)) {
    return null;
  }
  const kind = String(att.kind || '').toLowerCase();
  const mime = String(att.mimeType || att.contentType || '').toLowerCase();
  if (kind === 'image' || mime.startsWith('image/')) {
    return 'image';
  }
  if (kind === 'video' || mime.startsWith('video/')) {
    return 'video';
  }
  if (kind === 'audio' || mime.startsWith('audio/')) {
    return 'audio';
  }
  return null;
}

/**
 * @param {File} file
 * @returns {'image' | 'video' | 'audio' | null}
 */
export function localFileMediaPreviewVariant(file) {
  if (!file || typeof file.type !== 'string') {
    return null;
  }
  if (localStickerPresentationMode(file)) {
    return null;
  }
  const m = file.type.toLowerCase();
  if (m.startsWith('image/')) {
    return 'image';
  }
  if (m.startsWith('video/')) {
    return 'video';
  }
  if (m.startsWith('audio/')) {
    return 'audio';
  }
  return null;
}

export function attachmentIdsFingerprint(message) {
  return parseAttachments(message)
    .map((a) => {
      if (!a || typeof a !== 'object') {
        return '';
      }
      if (a.fileId) {
        return String(a.fileId);
      }
      if (a.documentId != null) {
        return String(a.documentId);
      }
      return '';
    })
    .filter(Boolean)
    .join(',');
}

export function conversationContentTypeFromMime(mime) {
  const m = String(mime || '').toLowerCase();
  if (m === STICKER_MIME_TYPE) {
    return 'document';
  }
  if (m === 'application/json' || m === 'application/zip') {
    return 'document';
  }
  if (m.startsWith('image/')) {
    return 'image';
  }
  if (m.startsWith('video/')) {
    return 'video';
  }
  if (m.startsWith('audio/')) {
    return 'audio';
  }
  if (m.startsWith('text/')) {
    return 'text';
  }
  return 'document';
}

/**
 * @param {File} file
 * @returns {Promise<{ mimeType: string, data: string, filename: string }>}
 */
export function readFileAsBase64Payload(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const dataUrl = String(fr.result || '');
      const comma = dataUrl.indexOf(',');
      const data = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
      const mimeType = file.type || 'application/octet-stream';
      resolve({ mimeType, data, filename: file.name || 'upload' });
    };
    fr.onerror = () => reject(fr.error || new Error('read failed'));
    fr.readAsDataURL(file);
  });
}

/**
 * @param {string} path
 * @returns {Promise<Blob>}
 */
export async function fetchAuthorizedMediaBlob(path) {
  const p = String(path || '').trim();
  if (!p) {
    throw new Error('missing media path');
  }
  const token = String(getAuthToken() || '').trim();
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(`${getApiBaseUrl()}${p}`, { headers });
  if (!response.ok) {
    throw new Error(`media ${response.status}`);
  }
  return response.blob();
}
